import type {
  RatingWithColor,
  RatingWithColorAndLikelihoodImpact,
} from '@risksmart-app/components/src/hooks/types';
import { DEFAULT_RISK_ASSESSMENT_RESULT_CONFIG } from '@risksmart-app/domain/src/types/risk-assessment-result-config';
import { useCallback, useMemo } from 'react';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';
import type { RiskAssessmentResultConfig } from '@/hooks/queries/risk-assessment-result-config/useGetLatestRiskAssessmentResultConfig';
import { useGetLatestRiskAssessmentResultConfig } from '@/hooks/queries/risk-assessment-result-config/useGetLatestRiskAssessmentResultConfig';
import { useGetRiskAssessmentResultConfigByIds } from '@/hooks/queries/risk-assessment-result-config/useGetRiskAssessmentResultConfigByIds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { bandDimensionValue, bandRatingCell } from './bandedRatingLookups';

export interface MatrixCell {
  label: string;
  value: number;
  color: string;
  likelihood: number;
  impact: number;
}

const toRatingOptions = (
  matrix: RiskAssessmentResultConfig['matrix']
): RatingWithColorAndLikelihoodImpact<number>[] =>
  matrix.map((entry) => ({
    label: entry.title,
    value: entry.value,
    color: entry.color,
    likelihoodImpact: [{ likelihood: entry.likelihood, impact: entry.impact }],
    range: [0, 0] as readonly [number, number],
  }));

const toLikelihoodImpactOptions = (
  ratings: { title: string; value: number; color: string }[]
): RatingWithColor<number>[] =>
  ratings.map((r) => ({
    label: r.title,
    value: r.value,
    color: r.color,
  }));

// Value-deduped rating options, ascending — many matrix cells share a value, so
// keep the first occurrence.
const buildRatingOptionsFromMatrix = (
  matrix: RiskAssessmentResultConfig['matrix']
): RatingWithColor<number>[] => {
  const seen = new Map<number, RatingWithColor<number>>();
  for (const entry of matrix) {
    if (!seen.has(entry.value)) {
      seen.set(entry.value, {
        label: entry.title,
        value: entry.value,
        color: entry.color,
      });
    }
  }

  return Array.from(seen.values()).sort((a, b) => a.value - b.value);
};

// Dedupe by rating value, keeping the first occurrence (many cells share one).
const buildRatingByValueLookup = (
  matrix: RiskAssessmentResultConfig['matrix']
): Map<number, RatingWithColor<number>> => {
  const map = new Map<number, RatingWithColor<number>>();
  for (const entry of matrix) {
    if (!map.has(entry.value)) {
      map.set(entry.value, {
        label: entry.title,
        value: entry.value,
        color: entry.color,
      });
    }
  }

  return map;
};

// Likelihood, impact and the matrix are kept as option scales rather than
// exact-value maps: a fractional score is banded to the declared value at or
// below it, which no key lookup can do. Stored rating values stay exact — the
// server only ever writes a declared value.
interface ConfigLookups {
  matrixOptions: RatingWithColorAndLikelihoodImpact<number>[];
  likelihoodOptions: RatingWithColor<number>[];
  impactOptions: RatingWithColor<number>[];
  ratingByValue: Map<number, RatingWithColor<number>>;
}

const buildConfigLookups = (
  config: RiskAssessmentResultConfig
): ConfigLookups => ({
  matrixOptions: toRatingOptions(config.matrix),
  likelihoodOptions: toLikelihoodImpactOptions(config.likelihood.ratings),
  impactOptions: toLikelihoodImpactOptions(config.impact.ratings),
  ratingByValue: buildRatingByValueLookup(config.matrix),
});

export const useScoringSettings = (options: {
  configType: ConfigType;
  configIds?: string[];
  skip?: boolean;
}) => {
  const scoringSettingsDataEnabled = useIsFeatureFlagEnabled(
    'scoring_settings_data'
  );
  const skipQueries = !scoringSettingsDataEnabled || options.skip === true;
  const {
    config: rawConfig,
    loading,
    id: latestConfigId,
  } = useGetLatestRiskAssessmentResultConfig({
    type: options.configType,
    skip: skipQueries,
  });
  // Flag on: fall back to the code default when no config is stored, so every
  // flag-on org resolves via config-or-default rather than the legacy taxonomy.
  // Flag off is unchanged.
  const latestConfig = scoringSettingsDataEnabled
    ? (rawConfig ?? DEFAULT_RISK_ASSESSMENT_RESULT_CONFIG)
    : undefined;
  const hasScoringSettings = !!latestConfig;

  // The latest config is already seeded into lookupCache below, so exclude it
  // from the by-id fetch (which is cache-and-network and would otherwise
  // refetch it). Only historical config versions need fetching.
  const historicalConfigIds = useMemo(
    () => (options.configIds ?? []).filter((id) => id !== latestConfigId),
    [options.configIds, latestConfigId]
  );

  const { configById, loading: configsByIdLoading } =
    useGetRiskAssessmentResultConfigByIds(historicalConfigIds, {
      skip: skipQueries,
    });

  const latestLookups = useMemo<ConfigLookups>(
    () =>
      latestConfig
        ? buildConfigLookups(latestConfig)
        : {
            matrixOptions: [],
            likelihoodOptions: [],
            impactOptions: [],
            ratingByValue: new Map<number, RatingWithColor<number>>(),
          },
    [latestConfig]
  );

  const lookupCache = useMemo(() => {
    const cache = new Map<string, ConfigLookups>();
    if (latestConfigId && latestConfig) {
      cache.set(latestConfigId, latestLookups);
    }
    configById.forEach((c, id) => {
      cache.set(id, buildConfigLookups(c));
    });

    return cache;
  }, [configById, latestConfigId, latestConfig, latestLookups]);

  const getLookupsForConfig = useCallback(
    (configId: string | null | undefined): ConfigLookups | undefined => {
      if (configId) {
        const cached = lookupCache.get(configId);
        if (cached) {
          return cached;
        }

        // The version is associated with the rating but not yet cached. While
        // that fetch is in flight, withhold rather than flash the latest
        // matrix's labels/colours. Once it has settled and the id still isn't
        // present (e.g. a config that was never fetched), fall back to latest so
        // a genuinely-rated record still resolves instead of showing "Unrated".
        return configsByIdLoading ? undefined : latestLookups;
      }

      return latestLookups;
    },
    [lookupCache, latestLookups, configsByIdLoading]
  );

  // Banded, not exact-cell matched (RSP-7284): a fractional pair left by the
  // 'none' rounding mode lands on the declared cell at or below it, exactly as
  // the server resolves the stored rating. A whole-number pair on declared
  // coordinates bands to itself, so it resolves the cell it always did.
  const getRatingByLikelihoodAndImpact = useCallback(
    (
      likelihood: number,
      impact: number,
      configId?: string | null
    ): MatrixCell | undefined => {
      const lookups = getLookupsForConfig(configId);
      if (!lookups) {
        return undefined;
      }
      const result = bandRatingCell(lookups.matrixOptions, likelihood, impact);
      if (result.status !== 'resolved') {
        return undefined;
      }

      return {
        label: result.rating.label,
        value: result.rating.value,
        color: result.rating.color,
        likelihood: result.bandedLikelihood,
        impact: result.bandedImpact,
      };
    },
    [getLookupsForConfig]
  );

  const getLikelihoodByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined => {
      const options = getLookupsForConfig(configId)?.likelihoodOptions;

      return options ? bandDimensionValue(options, value) : undefined;
    },
    [getLookupsForConfig]
  );

  const getImpactByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined => {
      const options = getLookupsForConfig(configId)?.impactOptions;

      return options ? bandDimensionValue(options, value) : undefined;
    },
    [getLookupsForConfig]
  );

  // Resolve a rating value to its band for a specific config version.
  const getRiskRatingByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined =>
      getLookupsForConfig(configId)?.ratingByValue.get(value),
    [getLookupsForConfig]
  );

  const riskRatingOptions = useMemo<RatingWithColor<number>[]>(
    () =>
      latestConfig ? buildRatingOptionsFromMatrix(latestConfig.matrix) : [],
    [latestConfig]
  );

  // Resolve a config version by id. An absent or latest id resolves to the
  // latest config. A historical id resolves to its loaded config; while that
  // fetch is in flight, withhold (undefined) rather than flash the latest
  // version, falling back to latest only once it has settled.
  const getConfigByVersion = useCallback(
    (configId?: string | null): RiskAssessmentResultConfig | undefined => {
      if (!configId || configId === latestConfigId) {
        return latestConfig;
      }

      const loaded = configById.get(configId);
      if (loaded) {
        return loaded;
      }

      return configsByIdLoading ? undefined : latestConfig;
    },
    [configById, latestConfig, latestConfigId, configsByIdLoading]
  );

  // Dropdown options for a likelihood/impact/riskRating field, resolved against
  // a specific config version, so a read-only stale rating shows the labels and
  // colours it was assessed under.
  const getOptionsByCategory = useCallback(
    (
      category: 'likelihood' | 'impact' | 'riskRating',
      configId?: string | null
    ): RatingWithColor<number>[] => {
      const config = getConfigByVersion(configId);
      if (!config) {
        return [];
      }
      switch (category) {
        case 'likelihood':
          return toLikelihoodImpactOptions(config.likelihood.ratings);
        case 'impact':
          return toLikelihoodImpactOptions(config.impact.ratings);
        case 'riskRating':
          return buildRatingOptionsFromMatrix(config.matrix);
      }
    },
    [getConfigByVersion]
  );

  // Impact categories for a specific config version, so a stale multi-impact
  // rating maps its stored impacts onto the categories it was assessed under.
  const getImpactCategories = useCallback(
    (configId?: string | null) =>
      getConfigByVersion(configId)?.impact.categories ?? [],
    [getConfigByVersion]
  );

  const impactCategories = latestConfig?.impact.categories ?? [];
  const hasMultipleImpacts = hasScoringSettings && impactCategories.length >= 2;

  return {
    hasScoringSettings,
    hasMultipleImpacts,
    loading,
    latestConfigId,
    config: latestConfig,
    matrixOptions: latestLookups.matrixOptions,
    likelihoodOptions: latestLookups.likelihoodOptions,
    impactOptions: latestLookups.impactOptions,
    riskRatingOptions,
    impactCategories,
    getRatingByLikelihoodAndImpact,
    getLikelihoodByValue,
    getImpactByValue,
    getRiskRatingByValue,
    getOptionsByCategory,
    getImpactCategories,
  };
};
